import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';
import { toast } from '@/hooks/errors/use-toast';

/**
 * Hook to manage page project assignments
 * Updates workbook_id in workbook_page_content table
 */
export const useAddPageToProject = (pageId: string) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Get current project assignment for this page
  const { data: pageData } = useQuery({
    queryKey: ['page-project-assignment', pageId],
    queryFn: async () => {
      if (!pageId) return null;
      
      const { data, error } = await supabase
        .from('workbook_page_content')
        .select('workbook_id, user_id')
        .eq('id', pageId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!pageId
  });

  const currentProjectId = pageData?.workbook_id || null;
  const isOwner = pageData?.user_id === user?.id;

  // Mutation to update page project assignment
  const updatePageProjectMutation = useMutation({
    mutationFn: async (projectId: string | null) => {
      if (!pageId) throw new Error('Page ID is required');
      if (!user?.id) throw new Error('User must be authenticated');

      console.log('[useAddPageToProject] Updating page project assignment:', { pageId, projectId });

      const { data, error } = await supabase
        .from('workbook_page_content')
        .update({ workbook_id: projectId })
        .eq('id', pageId)
        .eq('user_id', user.id) // Ensure user owns the page
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data, projectId) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['private-pages'] });
      queryClient.invalidateQueries({ queryKey: ['page-project-assignment', pageId] });
      queryClient.invalidateQueries({ queryKey: ['project-navigation'] });
      queryClient.invalidateQueries({ queryKey: ['workbook-pages'] });
      
      // CRITICAL: Invalidate the infinite user pages sidebar cache
      queryClient.invalidateQueries({ queryKey: ['infinite-user-pages'] });

      // Show success toast
      if (projectId) {
        toast({
          title: "Page Added to Project",
          description: "The page has been successfully added to the project.",
          variant: "default"
        });
      } else {
        toast({
          title: "Page Removed from Project", 
          description: "The page has been removed from the project.",
          variant: "default"
        });
      }
    },
    onError: (error) => {
      console.error('[useAddPageToProject] Error updating page project:', error);
      toast({
        title: "Error",
        description: "Failed to update page project assignment. Please try again.",
        variant: "destructive"
      });
    }
  });

  // Convenience methods
  const updatePageProject = (projectId: string | null) => {
    return updatePageProjectMutation.mutateAsync(projectId);
  };

  const removeFromProject = () => {
    return updatePageProjectMutation.mutateAsync(null);
  };

  return {
    currentProjectId,
    isOwner,
    updatePageProject,
    removeFromProject,
    isPending: updatePageProjectMutation.isPending
  };
}; 